import {
  Box,
  Card,
  CardContent,
  Divider,
  Icon,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';

import InfoTooltipIcon from '@/components/icons/InfoTooltipIcon';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import Co2RoundedIcon from '@mui/icons-material/Co2Rounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import WbCloudyRoundedIcon from '@mui/icons-material/WbCloudyRounded';
import Avatar from '@mui/material/Avatar';
import { Link } from 'react-router-dom';
import { formatNumber } from '@/utils/formatters';
import { useTranslation } from 'react-i18next';

const DeclarationCard = ({
  children,
  mode,
  setMode,
  selected,
  icon,
  energySummary,
  areaType,
}) => {
  const { t } = useTranslation('energyxplorer-esg');

  const label = t(`decleration_cards.${areaType}.label`);
  const description = t(`decleration_cards.${areaType}.description`);

  return (
    <Box
      onClick={() => {
        setMode(mode);
      }}
      sx={{ cursor: !selected ? 'pointer' : '', height: '100%' }}
    >
      <Card
        variant="outlined"
        sx={{
          background: selected ? '' : 'transparent',
          opacity: selected ? 1 : 0.5,
          p: 1,
          borderRadius: 2,
          borderColor: selected ? 'darkgray' : '',
          minHeight: 240,
          height: '100%',
        }}
      >
        <CardContent sx={{ height: '100%' }}>
          <Stack
            direction="column"
            color="inherit"
            component={Card}
            elevation={0}
            sx={{
              transition: 'box-shadow 0.3s ease',
              height: '100%',
              background: 'transparent',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    fontWeight="bold"
                  >
                    {label}
                  </Typography>
                  <InfoTooltipIcon
                    title={
                      <>
                        {description}{' '}
                        <Link
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            height: '100%',
                          }}
                          to={`https://energinet.dk/data-om-energi/data-til-dit-klimaregnskab/`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <OpenInNewRoundedIcon sx={{ color: 'lightgray' }} />
                        </Link>
                      </>
                    }
                  />
                </Box>
              </Box>
              <Box
                sx={{ display: 'flex', alignItems: 'center', height: '100%' }}
              >
                <Avatar
                  sx={{
                    width: 56,
                    height: 56,
                    backgroundColor: 'transparent',
                    borderRadius: '50%',
                    border: selected ? '2px solid green' : '2px solid gray',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Icon
                    fontSize={'large'}
                    sx={{
                      height: 'auto',
                      width: 'auto',
                      fontSize: 'large',
                      color: selected ? 'green' : 'gray',
                    }}
                  >
                    {icon}
                  </Icon>
                </Avatar>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-around',
                width: '100%',
                flexDirection: 'column',
              }}
            >
              <Box flexDirection={'row'} display={'flex'} alignItems={'center'}>
                <ValueDisplay
                  label={t('emission')}
                  value={formatNumber(
                    energySummary?.EnergyConsumptionEmissions / 1000,
                  )}
                  icon={<Co2RoundedIcon sx={{ mr: 1, color: 'gray' }} />}
                  unit="kg CO2e"
                />
                {areaType !== 'market' && (
                  <InfoTooltipIcon
                    title={
                      <Box>
                        {t(`decleration_cards.${areaType}.co2eExplanation`, {
                          distributionLoss: formatNumber(
                            energySummary?.DistributionLossEmissions / 1000,
                          ),
                          distributionLossPct: formatNumber(
                            energySummary.DistributionLossPercentage,
                          ),
                          transmissionLoss: formatNumber(
                            energySummary?.TransmissionLossEmissions / 1000,
                          ),
                          transmissionLossPct: formatNumber(
                            energySummary.TransmissionLossPercentage,
                          ),
                        })
                          .split('\n')
                          .map((line, i) => (
                            <React.Fragment key={i}>
                              {line}
                              <br />
                            </React.Fragment>
                          ))}
                      </Box>
                    }
                  />
                )}
              </Box>

              <Divider flexItem sx={{ my: 1 }} />
              <Box flexDirection={'row'} display={'flex'} alignItems={'center'}>
                <ValueDisplay
                  label={t('decleration')}
                  value={formatNumber(energySummary?.CO2eEmissionsPerKWh)}
                  icon={<WbCloudyRoundedIcon sx={{ mr: 1, color: 'gray' }} />}
                  unit="g/kWh"
                />

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                <ValueDisplay
                  label={t('consumption')}
                  value={`${formatNumber(energySummary?.EnergyUsageEligible)}`}
                  icon={<BoltRoundedIcon sx={{ mr: 1, color: 'gray' }} />}
                  unit="kWh"
                />
                {areaType === 'municipality' && (
                  <InfoTooltipIcon
                    title={t(
                      `decleration_cards.${areaType}.consumptionExplanation`,
                    )}
                  />
                )}
              </Box>
            </Box>
            {children && (
              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {children}
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

const ValueDisplay = ({ label, value, icon, unit }) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {icon}
      <Box sx={{ ml: 1 }}>
        <Typography variant="body2" color="textSecondary">
          {label}
        </Typography>
        <Typography variant="body1" color="textPrimary" fontWeight="bold">
          {value} {unit}
        </Typography>
      </Box>
    </Box>
  );
};

export default DeclarationCard;
